import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

/**
 * Reusable search modal component with lazy loading and debounced search
 * Uses a portal to render outside the parent DOM tree, preventing parent modal issues
 */
const SearchModal = ({
  open,
  onClose,
  onSelect,
  title = 'Search',
  searchPlaceholder = 'Search...',
  fetchItems,
  columns = [],
  getItemId = (item) => item.id,
  emptyMessage = 'No items found',
  selectedId = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const modalRef = useRef(null);

  const ITEMS_PER_PAGE = 20;

  // Focus search input when modal opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setItems([]);
      setPage(1);
      setHasMore(true);
      loadItems('', 1, true);
    }
  }, [open]);

  // Handle escape key to close
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Load items with debouncing for search
  const loadItems = useCallback(async (search, pageNum, reset = false) => {
    if (!fetchItems) return;

    setLoading(true);
    try {
      const result = await fetchItems(search, pageNum, ITEMS_PER_PAGE);
      const newItems = result.items || result;
      const total = result.total ?? newItems.length;

      setTotalCount(total);

      if (reset) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }

      setHasMore(newItems.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchItems]);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPage(1);
      setItems([]);
      loadItems(value, 1, true);
    }, 300);
  };

  // Infinite scroll observer
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!loadMoreRef.current || !hasMore || loading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadItems(searchTerm, nextPage, false);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, page, searchTerm, loadItems]);

  const handleSelect = (item) => {
    onSelect(item);
    handleClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setItems([]);
    onClose();
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking directly on the backdrop
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      handleClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks inside the modal from bubbling
    e.stopPropagation();
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] overflow-y-auto"
      onMouseDown={handleBackdropClick}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" aria-hidden="true" />

      {/* Modal positioning */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Modal panel */}
        <div
          ref={modalRef}
          className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl"
          onMouseDown={handleModalClick}
          onClick={handleModalClick}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={handleClose}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Search Input */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
            {totalCount > 0 && (
              <p className="mt-2 text-sm text-gray-500">
                {items.length} of {totalCount} results
              </p>
            )}
          </div>

          {/* Results Table */}
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && !loading ? (
              <div className="px-6 py-12 text-center text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => {
                    const itemId = getItemId(item);
                    const isSelected = selectedId && itemId === selectedId;

                    return (
                      <tr
                        key={itemId}
                        onClick={() => handleSelect(item)}
                        className={`cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-50 hover:bg-indigo-100'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                          >
                            {column.render
                              ? column.render(item)
                              : item[column.key]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Load more trigger */}
            <div ref={loadMoreRef} className="h-4" />

            {/* Loading indicator */}
            {loading && (
              <div className="px-6 py-4 text-center">
                <div className="inline-flex items-center gap-2 text-gray-500">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Loading...
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside parent DOM tree
  return createPortal(modalContent, document.body);
};

export default SearchModal;
